import React, { useEffect, useMemo, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import DeleteConfirmationDialog from '../DeleteConfirmationDialog';

import ImageEditor from './ImageEditor';
import type { LocalImageEdit } from './ImageEditor';
import MainImageLibraryTile from './MainImageLibraryTile';
import type { MediaImage, RoomOption } from './types';
import { canPersistRoomImageHiddenState, getSyncedImagePublicIds } from './utils';

interface Props {
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  mediaImages: Array<MediaImage>;
  roomOptions: Array<RoomOption>;
  onDeleteImage: (publicId: string) => Promise<void> | void;
  onSaveImageEdit: (publicId: string, edit: LocalImageEdit) => void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
}

export default function MainImageLibrary({
  hiddenImagePublicIds,
  imageEdits,
  mediaImages,
  roomOptions,
  onDeleteImage,
  onSaveImageEdit,
  onToggleHiddenImage,
}: Props) {
  const [orderedImagePublicIds, setOrderedImagePublicIds] = useState<Array<string>>(() =>
    mediaImages.map((image) => image.publicId),
  );
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const [imagePendingEdit, setImagePendingEdit] = useState<MediaImage | undefined>();
  const mediaImagesByPublicId = useMemo(
    () => new Map(mediaImages.map((image) => [image.publicId, image])),
    [mediaImages],
  );
  const orderedImages = orderedImagePublicIds
    .map((publicId) => mediaImagesByPublicId.get(publicId))
    .filter((image): image is MediaImage => !!image);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    setOrderedImagePublicIds((currentPublicIds) => getSyncedImagePublicIds(currentPublicIds, mediaImages));
  }, [mediaImages]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedImagePublicIds((currentPublicIds) => {
      const oldIndex = currentPublicIds.indexOf(String(active.id));
      const newIndex = currentPublicIds.indexOf(String(over.id));

      if (oldIndex < 0 || newIndex < 0) {
        return currentPublicIds;
      }

      return arrayMove(currentPublicIds, oldIndex, newIndex);
    });
  };

  const handleCloseDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!imagePendingDeletion) {
      return;
    }

    try {
      await onDeleteImage(imagePendingDeletion.publicId);
      setImagePendingEdit((currentImage) =>
        currentImage?.publicId === imagePendingDeletion.publicId ? undefined : currentImage,
      );
      setImagePendingDeletion(undefined);
    } catch {
      // Error already handled upstream.
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ px: 3, py: 2.5 }}>
          <Typography variant="h5">Main image library</Typography>
          <Chip
            label={`${mediaImages.length} ${mediaImages.length === 1 ? 'image' : 'images'}`}
            color="info"
            variant="outlined"
          />
        </Stack>
        <Divider />

        {orderedImages.length > 0 ? (
          <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
            <SortableContext items={orderedImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2,
                  p: 3,
                }}
              >
                {orderedImages.map((image) => (
                  <MainImageLibraryTile
                    key={image.publicId}
                    canToggleHidden={canPersistRoomImageHiddenState(image)}
                    image={image}
                    isHidden={hiddenImagePublicIds.includes(image.publicId)}
                    onDeleteImage={setImagePendingDeletion}
                    onEditImage={setImagePendingEdit}
                    onToggleHiddenImage={onToggleHiddenImage}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No images have been added to this offer yet.
            </Typography>
          </Box>
        )}
      </Paper>

      <DeleteConfirmationDialog
        body="This will remove the image from the main image library and from any room image slots. This action cannot be undone."
        open={!!imagePendingDeletion}
        title="Delete image?"
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      <ImageEditor
        edit={imagePendingEdit ? imageEdits[imagePendingEdit.publicId] : undefined}
        image={imagePendingEdit}
        open={!!imagePendingEdit}
        roomOptions={roomOptions}
        onClose={() => setImagePendingEdit(undefined)}
        onDeleteImage={(image) => {
          setImagePendingDeletion(image);
        }}
        onSave={onSaveImageEdit}
      />
    </>
  );
}
